import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { DOCUMENT } from '@angular/common';
import { environment } from '@environments/environment';
import { Subject, Subscription, of } from 'rxjs';
import { takeUntil, distinctUntilChanged, defaultIfEmpty, finalize, filter } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { Account, AccountsService, IAccount } from '@services/accounts.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { OrganisationDataService } from '@services/organisation-data.service';
import { ChangeDetectorRef } from '@angular/core';
import { UpdateService } from '@services/update-service.service';

/**
 * Default Class
 */
@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {

	/** Collection of the Subscription Objects for this Component */
	private subscriptions: Map<string, Subscription> = new Map();

	/** Show the Spinner */
	public showSpinner: boolean = false
	/** Environment Variables */
	public env: any
	/** This Current User */
	public thisUser: WebAppUser
	/** Selected Account */
	public selectedAccount: IAccount
	/** Selected Account GUID */
	public selectedAccountGUID: any
	/** List of My Accounts */
	public myAccounts: IAccount[]
	/** Notifications for User Exist */
	public notificationsExist: boolean = false
	/** Observable Subject Finalise */
	private finalise = new Subject<void>();
	/** Cookies Accepted */
	public showCookieBanner: boolean;

	/** updateAvailable */
	public updateAvailable = false;

	/**
	 * Default Constructor
	 * @param auth Auth Service
	 * @param authenticationService authentication Service
	 * @param siteSubscriptions site Subscriptions
	 * @param organisationDataService organisationDataService
	 * @param accountsService accounts Service
	 * @param doc doc
	 * @param globalFunctions global Functions
	 * @param cookieService Cookie Service
	 * @param cdr ChangeDetectorRef
	 */
	constructor(
		private updateService: UpdateService,
		public auth: AuthService,
		private authenticationService: AuthenticationService,
		private siteSubscriptions: siteSubscriptions,
		private organisationDataService: OrganisationDataService,
		private accountsService: AccountsService,
		@Inject(DOCUMENT) private doc: Document,
		private globalFunctions: GlobalFunctions,
		private cookieService: CookieService,
		private cdr: ChangeDetectorRef
	) {
		// console.log(`${this.constructor.name}`)
		// console.log(`${this.constructor.name}`)
		this.updateService.updateAvailable$.subscribe(flag => {
			// console.log(`${this.constructor.name} => this.updateService.updateAvailable$.subscribe`)
			// console.log(`${this.constructor.name} => this.updateService.updateAvailable$.subscribe`)
			this.updateAvailable = flag;
		});

		this.globalFunctions.displaySpinner(false)

		this.env = environment

		try {
			this.auth.user$.pipe(
				takeUntil(this.finalise), distinctUntilChanged(),
				filter((authUser) => !!authUser)
			).subscribe({
				next: (authUser: any) => {
					if (!authUser.email_verified) {
						this.authenticationService.logout(this.doc, '/unverified');
					}
					//Update the logged on user
					if (authUser) {
						this.GetUser(authUser)
					}
					this.subscribeCurrentUser()
					//this.setupSubscriptions();
					// console.log("Im here");
				},
				error: (error) => { this.globalFunctions.HandleError(error, "Error: this.auth.user$", false) },
				//complete: () => { console.log("this.auth.user$.subscribe: Complete"); }
			})


		}
		catch (error) { console.error(error) }
	}

	/** reload */
	reload() {
		this.updateService.activateUpdate();
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
			this.unsubscribeFromAll()
		}
		catch { }
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {

		this.globalFunctions.showSpinner$.pipe(
		).subscribe((display: boolean) => {
			this.showSpinner = display
			this.cdr.detectChanges()
		})

		/** Show a banner for use of cookies */
		// this.cookieService.set('cookieAcknowledged', '');
		const cookieAcknowledged = this.cookieService.get('cookieAcknowledged');
		if (!cookieAcknowledged && this.env.production) {
			// Display the banner or perform any other action
			this.showCookieBanner = true;
		}
	}

	/**
	 * setupSubscriptions
	 */
	setupSubscriptions() {
		this.subscribeAccounts()
		this.subscribeAccountGUID()
	}

	/**
	 * Update User Login Information
	 * @param profile
	 * @returns
	 */
	GetUser(profile: any) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		try {
			let id: string = profile["sub"];
			if (id != "") {
				this.authenticationService.getUser$(id).pipe(
					takeUntil(fetchDataSubject),
					distinctUntilChanged(),
					finalize(() => {
						// Ensure the subscription is terminated and the subject is completed
						fetchDataSubject.next();
						fetchDataSubject.complete();
					})
				).subscribe({
					next: (user) => {
						// console.log("Here");
						if (user) { this.authenticationService.setCurrentUser(user); }
						else { this.authenticationService.CreateUser(profile); }
					},
					error: (error) => {
						if (error.status == 404) {
							this.authenticationService.CreateUser(profile);
						} else {
							this.globalFunctions.HandleError(error, "Error: this.authenticationService.getUser$", false);
						}
					}
					// complete: () => { console.log("this.authenticationService.getUser$.subscribe: Complete"); }
				});
			}
		}
		catch (error) { console.error(`updateLogin:${this.GetUser.name}:`, error) }
	}

	/**
	 * Get a list of accounts for this logged on user
	 * @param userPublicID
	 */
	getAccounts(userPublicID: string): void {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>()

		//let userName = profile['nickname']
		let showDisabled = false
		this.myAccounts = []

		let data = {
			"userPublicID": userPublicID,
			"showDisabled": showDisabled
		};

		this.siteSubscriptions.RefreshMyAccounts(data);

		// this.accountsService.getAccounts$(data).pipe(
		// 	takeUntil(fetchDataSubject),
		// 	distinctUntilChanged(),
		// 	finalize(() => {
		// 		// Ensure the subscription is terminated and the subject is completed
		// 		fetchDataSubject.next();
		// 		fetchDataSubject.complete();
		// 	})
		// ).subscribe({
		// 	next: (response: any) => {
		// 		try {
		// 			this.siteSubscriptions.setMyAccounts(response);
		// 		} catch (error) {
		// 			this.siteSubscriptions.setMyAccounts()
		// 			this.globalFunctions.HandleError(error, "getAccounts: this.siteSubscriptions.setMyAccounts")
		// 		}
		// 	},
		// 	error: (error) => {
		// 		this.siteSubscriptions.setMyAccounts()
		// 		if (error.status != 404) {
		// 			this.globalFunctions.HandleError(error, "getAccounts: Error retrieving Accounts")
		// 		}
		// 	},
		// 	//complete: () => { console.log("this.accountsService.getAccounts$.subscribe: Complete"); }
		// });
	}

	/**
	 * Get a list of accounts for this logged on user
	 */
	GetOrganisations(userPublicID: string, AccountGUID: string, OrganisationID: string = "") {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		/** Subscribe */
		let Data = {
			"userPublicID": userPublicID,
			"AccountGUID": AccountGUID,
			...(OrganisationID && { "OrganisationID": OrganisationID })
		}

		// Subscribe to getOrganisations
		this.organisationDataService.getOrganisations$(AccountGUID, OrganisationID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (response: any) => {
				try {
					this.siteSubscriptions.setmyOrganisations(response)
				} catch (error) {
					this.siteSubscriptions.setmyOrganisations()
					this.globalFunctions.HandleError(error, "")
				}
			},
			error: (error) => {
				console.error(error)
				this.siteSubscriptions.setmyOrganisations()
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "GetOrganisations: Error retrieving Organisations")
				}
			},
			//complete: () => { console.log("this.accountsService.getOrganisations$.subscribe: Complete"); }
		});
	}

	/**
	 * subscribeCurrentUser
	 */
	subscribeCurrentUser() {
		this.authenticationService.currentUser$.pipe(
			takeUntil(this.finalise), distinctUntilChanged(),
			filter((currentUser) => !!currentUser)
		).subscribe({
			next: (currentUser) => {
				this.thisUser = <WebAppUser>currentUser;
				if (this.thisUser.Disabled) {
					this.authenticationService.logout(this.doc, '/accountdisabled');
				}
				// Get the Accounts for this user
				this.getAccounts(this.thisUser.Sub);
				this.setupSubscriptions();
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.authenticationService.currentUser$", false) },
			//complete: () => { console.log("this.authenticationService.currentUser$.subscribe: Complete"); }
		})
	}

	/**
	 * subscribeAccountGUID
	 */
	subscribeAccountGUID() {
		try {
			// Unsubscribe from existing subscription to this specific observable if it exists
			this.unsubscribeFromObservable("selectedAccountGUID$");
			// Subscribe to selectedAccountGUID
			const subscription = this.siteSubscriptions.selectedAccountGUID$.pipe(
				takeUntil(this.finalise),
				distinctUntilChanged(),
				defaultIfEmpty(null), // emit null if no user is emitted
				// finalize(() => { })
			).subscribe({
				next: (accountGUID) => {
					if (accountGUID) {
						if (accountGUID != this.selectedAccountGUID) {
							this.selectedAccountGUID = accountGUID;
							if (this.myAccounts) {
								this.selectedAccount = <IAccount>this.myAccounts.find(({ GUID }) => GUID.toUpperCase() === accountGUID.toUpperCase())
								if (this.selectedAccount && this.thisUser) {
									this.GetOrganisations(this.thisUser.Sub, accountGUID);
								}
							}
						}
					} else {
						if (this.selectedAccountGUID) { this.GetOrganisations(this.thisUser.Sub, this.selectedAccountGUID) }
					}
				},
				error: (error) => { this.globalFunctions.HandleError(error, "Error: this.siteSubscriptions.selectedAccountGUID$", false) },
				//complete: () => { console.log("this.siteSubscriptions.selectedAccountGUID$.subscribe: Complete"); }
			});
			// Store the subscription in the array
			this.subscriptions.set('selectedAccountGUID$', subscription);
		}
		catch (error) { console.error(error) }
	}

	/**
	 * subscribeAccounts
	 */
	subscribeAccounts() {
		try {
			// Unsubscribe from existing subscription to this specific observable if it exists
			this.unsubscribeFromObservable("myAccounts$");
			// Subscribe to myAccounts
			const subscription = this.siteSubscriptions.myAccounts$.pipe(
				takeUntil(this.finalise),
				distinctUntilChanged(),
				filter((myAccounts) => !!myAccounts)
				// defaultIfEmpty(null), // emit null if no user is emitted
				// finalize(() => { })
			).subscribe({
				next: (myAccounts) => {
					this.myAccounts = myAccounts;
					this.myAccounts = this.siteSubscriptions.normalizeFeatures(this.myAccounts) as Account[];
				},
				error: (error) => { this.globalFunctions.HandleError(error, "Error: this.siteSubscriptions.myAccounts$", false) },
				//complete: () => { console.log("this.siteSubscriptions.myAccounts$.subscribe: Complete"); }
			});
			// Store the subscription in the array
			this.subscriptions.set('myAccounts$', subscription);

		}
		catch (error) { console.error(error) }
	}

	/**
	 * acknowledgeCookies
	 */
	acknowledgeCookies() {
		// Set the cookie to acknowledge
		this.cookieService.set('cookieAcknowledged', 'true', 30);
		// Hide the banner
		this.showCookieBanner = false;
	}

	/**
	 * Method to unsubscribe from a specific observable by key
	 */
	private unsubscribeFromObservable(key: string) {
		try {
			const subscription = this.subscriptions.get(key);
			if (subscription) {
				subscription.unsubscribe();
				this.subscriptions.delete(key);
			}
		} catch (error) { }
	}

	/**
	 * Method to unsubscribe from all subscriptions
	 */
	unsubscribeFromAll() {
		try {
			this.subscriptions.forEach(subscription => subscription.unsubscribe());
			this.subscriptions.clear();
		} catch (error) { }
	}

}
